import { View, Text, StyleSheet } from "@react-pdf/renderer";
import type { PdfWidgetSnapshot } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";

const s = StyleSheet.create({
  cell: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#b9cfec",
    borderRadius: 4,
    backgroundColor: "#f7fafd",
    overflow: "hidden",
  },
  title: { fontWeight: 700, color: "#2d5c9e" },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderTopWidth: 1,
    borderTopColor: "#f6ddc6",
  },
  rowLabel: { color: "#5b83b8", fontWeight: 600 },
  // Left-aligned: the value is a full name list that wraps — ragged-left reads best.
  rowValue: { color: "#2d5c9e", flex: 1 },
});

/**
 * The controller has no data of its own, so the PDF shows what it was SET TO — otherwise a
 * printed board carries widgets scoped by a control the reader cannot see. Rendered in the
 * same grayed blue the governed sections use on screen, so the link reads at a glance.
 */
export function renderDynamicController(
  snapshot: Extract<PdfWidgetSnapshot, { type: "slicer" }>,
  rect: PdfRect,
  scale: number,
) {
  // The card is reference material, not a headline: the title sits barely above the body,
  // and the body carries the actual selections. A fixed 10pt title next to scaled 7pt rows
  // read as a poster — deliberately off the house fixed-title convention here.
  const pad = Math.max(6, 8 * scale);
  const titleSize = Math.max(8, 9 * scale);
  const bodySize = Math.max(7, 8 * scale);
  const gap = Math.max(3, 4 * scale);
  const labelWidth = Math.max(42, 48 * scale);

  // A tile can now shrink to spacer size, where the title-plus-rows layout cannot fit and
  // would clip mid-glyph. Mirror the on-screen chip face instead: one paragraph — bold
  // title, then the values — clamped to the lines that fit, ending in an ellipsis.
  const titleHeight = titleSize * 1.4;
  const rowHeight = bodySize * 1.4 + gap * 2;
  const fullNeeds = pad * 2 + titleHeight + snapshot.rows.length * rowHeight;
  if (rect.height < fullNeeds || rect.width < 110) {
    const chipSize = Math.max(6.5, 7.5 * scale);
    const chipPad = Math.max(3, 4 * scale);
    const maxLines = Math.max(1, Math.floor((rect.height - chipPad * 2) / (chipSize * 1.35)));
    const values = snapshot.rows.map((row) => row.value).join(" · ");
    return (
      <View
        style={[
          s.cell,
          {
            left: rect.left, top: rect.top, width: rect.width, height: rect.height,
            padding: chipPad, justifyContent: "center",
          },
        ]}
      >
        <Text
          style={{ fontSize: chipSize, color: "#2d5c9e", maxLines, textOverflow: "ellipsis" }}
        >
          <Text style={{ fontWeight: 700 }}>{snapshot.title || "Slicer"}</Text>
          {values ? ` · ${values}` : ""}
        </Text>
      </View>
    );
  }

  return (
    <View
      style={[
        s.cell,
        { left: rect.left, top: rect.top, width: rect.width, height: rect.height, padding: pad },
      ]}
    >
      <Text style={[s.title, { fontSize: titleSize }]}>
        {snapshot.title || "Slicer"}
      </Text>

      {snapshot.rows.map((row) => (
        <View
          key={row.label}
          style={[s.row, { marginTop: gap, paddingTop: gap }]}
        >
          <Text style={[s.rowLabel, { fontSize: bodySize, width: labelWidth }]}>{row.label}</Text>
          <Text style={[s.rowValue, { fontSize: bodySize, marginLeft: gap }]}>{row.value}</Text>
        </View>
      ))}
    </View>
  );
}
